import type { LoaderFunction } from '@remix-run/node'
import { json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { NS } from '~/enums'
import DownloadIcon from '~/components/icons/Download'
import { handleError } from '~/services/error-handler.server'
import ApiClient from '~/services/api-client.server'
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton } from 'react-share'
import { QCStorage } from '~/components/quick-checkout/QCsessionStorage'

type LoaderData = {
  documentDetails: any //TODO: replace type
}

export let loader: LoaderFunction = async ({ request, params }) => {
  // create headers
  let headers = new Headers()
  // await checkAuth(request, new Headers(), { successRedirect: '/' }, false)

  const documentExternalId = params.id as string

  const apiClient = new ApiClient(null, request.headers)

  let documentDetails

  try {
    const response = await apiClient.getCertificateDocumentDetailsById(documentExternalId)
    documentDetails = response.data
  } catch (error: any) {
    const handledError = await handleError(error, request)

    throw new Response(handledError.message, {
      status: handledError.response?.status ?? 500,
      headers: headers,
    })
  }

  return json<LoaderData>(
    {
      documentDetails,
    },
    { headers: headers },
  )
}

export default function CertificatePage() {
  const { documentDetails } = useLoaderData<LoaderData>()

  let { t } = useTranslation()

  let certificateVerified = t('certificate_verified', { ns: NS.CHALLENGES })
  const startChallenge = t('start_challenge', { ns: NS.GLOBALS })
  const readyforMore = t('ready_for_more', { ns: NS.CHALLENGES })
  const download = t('download', { ns: NS.GLOBALS })
  const shareViaSocialMedia = t('share_via_social_media', { ns: NS.CHALLENGES })

  const redditShareUrl = documentDetails?.documentUrl
    ? `https://www.reddit.com/submit?url=${encodeURIComponent(documentDetails.documentUrl)}`
    : 'https://www.reddit.com/submit'

  return (
    <div className="container mx-auto flex max-w-lg flex-col items-center gap-8 px-4 py-12">
      <div className="flex w-fit flex-row items-center justify-center gap-2 rounded-md bg-success-300 p-4 text-sm text-base-400 ring-1 ring-success-750">
        <img src="/images/certificate/verified.svg" alt="verified" />
        <span>{certificateVerified}</span>
      </div>
      <img src={documentDetails?.documentUrl} alt="certificate" />

      <div className="flex w-full flex-row justify-between">
        <div className="flex flex-col gap-2">
          <span className="font-normal">{shareViaSocialMedia}</span>
          <div className="flex flex-row gap-4">
            <FacebookShareButton url={documentDetails?.documentUrl} className="Demo__some-network__share-button">
              <img
                // IMPORTANT: id for GTM
                id="social-facebook-btn"
                src={'/images/certificate/facebook.svg'}
                width={18}
              />
            </FacebookShareButton>
            <TwitterShareButton url={documentDetails?.documentUrl} className="Demo__some-network__share-button">
              <img
                // IMPORTANT: id for GTM
                id="social-x-twitter-btn"
                src={'/images/certificate/x-twitter.svg'}
                width={18}
              />
            </TwitterShareButton>
            <LinkedinShareButton url={documentDetails?.documentUrl} className="Demo__some-network__share-button">
              <img
                // IMPORTANT: id for GTM
                id="social-linkedin-btn"
                src={'/images/certificate/linkedin.svg'}
                width={18}
              />
            </LinkedinShareButton>
            <a
              href={redditShareUrl}
              className="Demo__some-network__share-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                // IMPORTANT: id for GTM
                id="social-reddit-btn"
                src={'/images/certificate/reddit.svg'}
                width={18}
              />
            </a>
          </div>
        </div>
        <button
          // IMPORTANT: id for GTM
          id="download-certificate-btn"
          className="btn btn-purple-gradient flex cursor-pointer flex-row justify-center gap-2"
          onClick={() => window.open(documentDetails?.documentUrl)}
        >
          <DownloadIcon className="text-base-100" />
          <span className="font-semibold text-base-100">{download}</span>
        </button>
      </div>

      <div className="flex flex-col items-center gap-2">
        <span className="text-sm font-semibold">{readyforMore}</span>
        <Link
          to="/challenges"
          className="btn btn-success-gradient font-semibold"
          onClick={() => {
            QCStorage.clearAllCheckoutData()
          }}
        >
          <span className="pointer-events-none">{startChallenge}</span>
        </Link>
      </div>
    </div>
  )
}
